/**
 * KycRadioGroup - KYC 单选组组件
 *
 * @description
 * 专为 KYC 流程设计的单选组组件，支持标准选项和自定义输入选项。
 * 当选项包含自定义输入时，组件会返回用户输入的值而非选项的原始值。
 *
 * @usage
 * ```tsx
 * // 基础用法
 * <KycRadioGroup
 *   label="选择选项"
 *   value={selectedValue}
 *   options={[
 *     { value: 'option1', title: '选项1' },
 *     { value: 'option2', title: '选项2' },
 *   ]}
 *   onValueChange={(value) => setSelectedValue(value)}
 * />
 * ```
 */

import React, { memo, useCallback, useEffect, useState } from "react";
import { StyleSheet, Text, View } from "../platformComponents";
import type { IKycRadioGroupProps, IOptionItem } from "@adgm/cross-models";
import {
  colors,
  flexDirection,
  fontFamily,
  fontSize,
  height,
  margin,
  padding,
  width,
} from "../styles/common";
import { AsFormItemLayout } from "./AsFormItemLayout";
import { i18nCrossUtil, IS_WEB, tranformPx } from "@adgm/libs";
import { CommonInput, CommonRadio } from "../common";
import { KycSelect } from "./KycSelect";
import { CUSTOM_RADIO_WITH_INPUT, CUSTOM_RADIO_WITH_SELECT } from "@adgm/cross-config";
import { KycLabelElement } from "./KycLabelElement";
import { useGenericFormContext } from "./GenericForm";

/**
 * KycRadioGroup - 支持自定义输入的单选组组件
 *
 * @param props 组件属性配置
 * @returns 单选组组件 JSX 元素
 */
export const KycRadioGroup = memo((props: IKycRadioGroupProps) => {
  const {
    value,
    label,
    error,
    options,
    style,
    sequenceNumber,
    tip,
    hideErrorSpace,
    onValueChange,
    customSelectOptions,
    required,
  } = props;
  const [selectedOption, setSelectedOption] = useState<any>();
  const [customInputValue, setCustomInputValue] = useState<string>("");
  const [customSelectValue, setCustomSelectValue] = useState<string | undefined>(undefined);
  const crossIntl = i18nCrossUtil.t();
  const { genericFormType } = useGenericFormContext();
  const isQuestionnaireForm = genericFormType === "questionnaire";
  useEffect(() => {
    if (!value) {
      setSelectedOption("");
      setCustomInputValue("");
      setCustomSelectValue("");
      return;
    }

    const standardOption = options.find(option => option.value === value);
    if (standardOption) {
      setSelectedOption(value);
      setCustomInputValue("");
      setCustomSelectValue("");
      return;
    }

    // 检查是否是自定义选项的选项值（刚选中但还没输入/选择）
    const otherOption = options.find(opt => opt.value === CUSTOM_RADIO_WITH_INPUT);
    const pleaseSelectOption = options.find(opt => opt.value === CUSTOM_RADIO_WITH_SELECT);

    if (otherOption && value === otherOption.value) {
      setSelectedOption(value);
      setCustomInputValue("");
      setCustomSelectValue("");
      return;
    }

    if (pleaseSelectOption && value === pleaseSelectOption.value) {
      setSelectedOption(value);
      setCustomInputValue("");
      setCustomSelectValue("");
      return;
    }

    // 如果 value 不匹配任何选项值，则认为是自定义输入或选择的内容
    if (otherOption) {
      setSelectedOption(otherOption.value);
      setCustomInputValue(value);
      setCustomSelectValue("");
      return;
    }

    if (pleaseSelectOption) {
      setSelectedOption(pleaseSelectOption.value);
      setCustomInputValue("");
      setCustomSelectValue(value);
    }
  }, [value, options]);

  /**
   * 处理 radio 选项变化
   * 根据不同的选项的类型，分别处理不同的逻辑
   * @param optionValue 选中的选项值
   */
  const handleRadioChange = useCallback(
    (optionValue: string) => {
      console.log(`选中了选项：${optionValue}`);
      const selectedOption = options.find(opt => opt.value === optionValue);
      setSelectedOption(optionValue);
      if (selectedOption?.value === CUSTOM_RADIO_WITH_INPUT) {
        const valueToEmit = customInputValue || optionValue;
        onValueChange?.(valueToEmit);
      } else if (selectedOption?.value === CUSTOM_RADIO_WITH_SELECT) {
        const valueToEmit = customSelectValue || optionValue;
        onValueChange?.(valueToEmit);
      } else {
        // 标准选项直接使用选项值
        setCustomInputValue("");
        setCustomSelectValue("");
        onValueChange?.(optionValue);
      }
    },
    [customInputValue, customSelectValue, onValueChange, options],
  );

  /**
   * 处理自定义输入值变化
   *
   * @param inputValue 用户输入的值
   */
  const handleCustomInputChange = useCallback(
    (inputValue: string) => {
      setCustomInputValue(inputValue);

      const otherOption = options.find(opt => opt.value === CUSTOM_RADIO_WITH_INPUT);
      if (otherOption) {
        if (inputValue.trim()) {
          // 有输入内容时，自动选中 "Other" 选项
          setSelectedOption(otherOption.value);
          onValueChange?.(inputValue);
        } else {
          // 清空输入时，取消选中
          setSelectedOption("");
          onValueChange?.("");
        }
      }
    },
    [onValueChange, options],
  );

  /**
   * 处理自定义下拉选择值变化
   *
   * @param selectValue 用户选择的值
   */
  const handleCustomSelectChange = useCallback(
    (selectValue: string) => {
      setCustomSelectValue(selectValue);
      const pleaseSelectOption = options.find(opt => opt.value === CUSTOM_RADIO_WITH_SELECT);
      if (pleaseSelectOption) {
        if (selectValue?.trim()) {
          // 有选择内容时，自动选中 "Please select" 选项
          setSelectedOption(pleaseSelectOption.value);
          onValueChange?.(selectValue);
        } else {
          // 清空选择时，取消选中
          setSelectedOption("");
        }
      }
    },
    [onValueChange, options],
  );

  const renderCustomChild = (item: IOptionItem) => {
    if (item.value === CUSTOM_RADIO_WITH_INPUT) {
      return (
        <View style={{ flex: 1 }}>
          {item.description ? (
            <Text style={IS_WEB ? styles.descriptionTextWeb : styles.descriptionTextApp}>
              {item.description}
            </Text>
          ) : null}
          <CommonInput
            value={customInputValue}
            style={[styles.customInput, item.inputStyle]}
            onValueChange={handleCustomInputChange}
            placeholderTextColor={IS_WEB ? colors.white21 : colors.white14}
            placeholder={props.placeholder || crossIntl["kyc.other.input"]}
            trim={false}
          />
        </View>
      );
    }

    if (item.value === CUSTOM_RADIO_WITH_SELECT) {
      return (
        <KycSelect
          style={{ paddingLeft: 0, marginTop: IS_WEB ? -20 : 0 }}
          value={customSelectValue}
          onValueChange={handleCustomSelectChange}
          options={customSelectOptions || []}
          showSearch
          hideErrorSpace
        />
      );
    }

    return null;
  };

  return (
    <AsFormItemLayout
      {...props}
      error={error}
      tip={tip}
      style={[styles.label]}
      hideErrorSpace={hideErrorSpace}
      containerStyle={{
        backgroundColor: colors.transparent,
      }}
      labelElement={
        <KycLabelElement label={label} sequenceNumber={sequenceNumber} required={required} />
      }>
      <View style={[styles.container, { paddingLeft: isQuestionnaireForm ? padding.p40 : 0 }]}>
        {options.map((item, index) => {
          const isChecked = selectedOption == item.value;
          return (
            <View
              style={index === options.length - 1 ? {} : styles.optionContainer}
              key={`${item.value}-${index}`}>
              <CommonRadio
                style={style}
                value={item.value}
                checked={isChecked}
                label={item.title}
                onChange={handleRadioChange}>
                {renderCustomChild(item)}
              </CommonRadio>
            </View>
          );
        })}
      </View>
    </AsFormItemLayout>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  optionContainer: {
    paddingBottom: padding.p24,
  },
  item: {
    flexDirection: flexDirection.row,
  },
  text: {
    flex: 1,
    fontSize: fontSize.f14,
  },
  icon: {
    width: width.w14,
    height: height.h14,
    marginTop: tranformPx(2.5),
    marginRight: margin.m13,
  },
  label: {
    fontSize: IS_WEB ? fontSize.f20 : fontSize.f16,
    fontFamily: IS_WEB ? "Common-Medium" : fontFamily.commonRegular,
  },
  customInput: {
    fontSize: fontSize.f14,
    flex: 1,
    fontFamily: IS_WEB ? "Common-Regular" : fontFamily.commonRegular,
    paddingVertical: IS_WEB ? padding.p14 : padding.p16,
    paddingHorizontal: IS_WEB ? padding.p14 : padding.p16,
    // marginTop: padding.p8,
    backgroundColor: IS_WEB ? colors.grey22 : colors.mainBackground,
  },
  labelItemText: {
    fontSize: IS_WEB ? fontSize.f16 : fontSize.f14,
    fontFamily: IS_WEB ? "Common-Regular" : fontFamily.commonRegular,
    color: colors.white,
    marginTop: margin.m3,
    marginBottom: margin.m28,
  },
  descriptionTextWeb: {
    flex: 1,
    fontSize: fontSize.f16,
    fontFamily: "Common-Regular",
    color: colors.white,
    display: "flex",
    alignItems: "center",
  },
  descriptionTextApp: {
    flex: 1,
    fontSize: fontSize.f14,
    fontFamily: fontFamily.commonRegular,
    color: colors.white,
  },
});
